import {
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  type BufferGeometry,
  type Material,
} from 'three';
import { addCyclistLegs, addPerson, personLook, type PersonLook, type PersonPose } from './person';
import { curve, PartList, type Finish, type P3 } from './vehicle-parts';

/** 車両の見た目の種類（`@rcs/data-schema` の `VehicleModel` と同じ名前）。 */
export type VehicleModelKind =
  | 'coaster'
  | 'steam-locomotive'
  | 'bullet-train'
  | 'maglev'
  | 'sports-car'
  | 'bicycle'
  | 'mine-cart'
  | 'bobsled'
  | 'tram';

/** 車両の 3D モデルを作るための仕様。 */
export interface VehicleModelSpec {
  readonly model: VehicleModelKind;
  /** 編成の車両数 */
  readonly carCount: number;
  /** 1 車両の長さ [m] */
  readonly carLength: number;
  /** 車両間の間隔 [m] */
  readonly carSpacing: number;
  /** 幅 [m] */
  readonly width: number;
  /** 高さ [m] */
  readonly height: number;
  /** 1 車両の座席数 */
  readonly seatsPerCar: number;
  /** 乗車率（0〜1）。乗せる人の数 = 座席数 × 乗車率（四捨五入） */
  readonly occupancy: number;
  /** 車体の色（`0xrrggbb`） */
  readonly color: number;
}

/**
 * 1 つの座席：乗客の腰（骨盤の中心）の位置。車両の局所座標（X = 右、Y = レールからの高さ、Z = 後方。
 * 前方は −Z）と、その席での姿勢。
 */
export interface Seat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly pose: PersonPose;
}

/** 隣り合う車両の中心の距離 [m]。 */
export function carPitch(spec: Pick<VehicleModelSpec, 'carLength' | 'carSpacing'>): number {
  return spec.carLength + spec.carSpacing;
}

/** 画面に描く乗客の最大数（1 車両あたり）。新幹線のように座席が多いときは、前の席から描く。 */
export const MAX_VISIBLE_RIDERS_PER_CAR = 12;

/** 乗客視点のカメラの高さ（レールの上、目の位置）[m]。モデルごと。 */
export function eyeHeightOf(model: VehicleModelKind): number {
  switch (model) {
    case 'coaster':
      return 1.35;
    case 'mine-cart':
      return 1.5;
    case 'steam-locomotive':
      return 3.1; // 運転台に立つ
    case 'bullet-train':
      return 2.3;
    case 'maglev':
      return 2.2;
    case 'tram':
      return 1.65;
    case 'sports-car':
      return 1.05;
    case 'bicycle':
      return 1.5;
    case 'bobsled':
      return 0.8;
  }
}

/** 先頭・最後尾の流線形の鼻の長さ [m]（新幹線・リニア）。 */
function noseLength(model: VehicleModelKind, carLength: number): number {
  if (model === 'bullet-train') return Math.min(10, carLength * 0.4);
  if (model === 'maglev') return Math.min(12, carLength * 0.5);
  return 0;
}

/**
 * 座席の配置（乗客の腰の位置と姿勢）。車両の中心を原点として、前方から座る。
 *
 * - コースター・トロッコ：2 人ずつ横に並び、前後に並ぶ。3 人に 1 人は両手を上げる
 * - 新幹線・リニア・路面電車・客車：通路をはさんで 2 + 2 席の並び（先頭車は鼻の後ろから）
 * - スポーツカー：右が運転席（ハンドルを握る）、左が助手席
 * - 自転車：サドルに 1 人（こぐ姿勢）
 * - ボブスレー：縦一列（前かがみ）
 * - 蒸気機関車：機関車は運転台に 2 人（機関士・助士が立つ）、炭水車は無人、客車は列車の並び
 */
export function seatLayout(spec: VehicleModelSpec, carIndex: number): readonly Seat[] {
  const { model, seatsPerCar, carLength: L, width: W } = spec;
  const count = Math.min(
    MAX_VISIBLE_RIDERS_PER_CAR,
    Math.max(0, Math.round(seatsPerCar * Math.min(1, Math.max(0, spec.occupancy)))),
  );
  const seats: Seat[] = [];
  /** 横に `columns`（左から）並べ、前後に `pitch` ずつ並べる */
  const rows = (
    columns: readonly number[],
    y: number,
    startZ: number,
    pitch: number,
    pose: (i: number) => PersonPose = () => 'seated',
  ): void => {
    for (let i = 0; i < count; i += 1) {
      const row = Math.floor(i / columns.length);
      seats.push({
        x: columns[i % columns.length] ?? 0,
        y,
        z: startZ + row * pitch,
        pose: pose(i),
      });
    }
  };
  /**
   * 列車の客室：`startZ` から `endZ` まで `pitch` ごとにある座席の列のうち、乗せる人の列を前後に散らして選ぶ
   * （乗客が車両の片側に寄らないように）。座席の列の位置は、車体の座席（{@link hollowBody}）とそろう。
   */
  const spreadRows = (
    columns: readonly number[],
    y: number,
    startZ: number,
    endZ: number,
    pitch: number,
  ): void => {
    const available = Math.max(1, Math.floor((endZ - startZ) / pitch + 1e-9) + 1);
    const riderRows = Math.ceil(count / columns.length);
    for (let i = 0; i < count; i += 1) {
      const row = Math.floor(i / columns.length);
      const index =
        riderRows > 1
          ? Math.round((row * (available - 1)) / (riderRows - 1))
          : Math.floor((available - 1) / 2);
      seats.push({
        x: columns[i % columns.length] ?? 0,
        y,
        z: startZ + Math.min(index, available - 1) * pitch,
        pose: 'seated',
      });
    }
  };
  const trainColumns = [-W * 0.32, -W * 0.14, W * 0.14, W * 0.32];
  switch (model) {
    case 'coaster':
    case 'mine-cart': {
      const rowCount = Math.max(1, Math.ceil(count / 2));
      // 足を前に伸ばして座るため、席の列は少し後ろ寄りに並べる（先頭の列の足が車体の先から出ない）
      const pitch = rowCount > 1 ? (L * 0.5) / (rowCount - 1) : 0;
      rows(
        [-W * 0.22, W * 0.22],
        model === 'coaster' ? 0.62 : 0.78,
        rowCount > 1 ? -L * 0.22 : 0.1,
        pitch,
        (i) => (model === 'coaster' && (i + carIndex) % 3 === 0 ? 'arms-up' : 'seated'),
      );
      break;
    }
    case 'bobsled': {
      const pitch = count > 1 ? (L * 0.55) / (count - 1) : 0;
      for (let i = 0; i < count; i += 1) {
        seats.push({ x: 0, y: 0.35, z: (count > 1 ? -L * 0.28 : 0) + i * pitch, pose: 'tuck' });
      }
      break;
    }
    case 'sports-car':
      for (let i = 0; i < Math.min(2, count); i += 1) {
        // 右（+X）が運転席
        seats.push({
          x: i === 0 ? W * 0.22 : -W * 0.22,
          y: 0.4,
          z: 0.35,
          pose: i === 0 ? 'driver' : 'seated',
        });
      }
      break;
    case 'bicycle':
      if (count > 0) seats.push({ x: 0, y: 0.95, z: 0.12, pose: 'cyclist' });
      break;
    case 'steam-locomotive':
      if (carIndex === 0) {
        for (let i = 0; i < Math.min(2, count); i += 1) {
          seats.push({ x: i === 0 ? W * 0.2 : -W * 0.2, y: 2.35, z: L * 0.36, pose: 'standing' });
        }
      } else if (carIndex >= 2) {
        spreadRows(trainColumns, 1.45, -L * 0.36, L * 0.36, 1.6);
      }
      break;
    case 'bullet-train':
    case 'maglev': {
      const frontNose = carIndex === 0 ? noseLength(model, L) : 0;
      const rearNose =
        carIndex === spec.carCount - 1 && spec.carCount > 1 ? noseLength(model, L) : 0;
      spreadRows(
        trainColumns,
        model === 'bullet-train' ? 1.55 : 1.45,
        -L / 2 + Math.max(frontNose + 1.5, L * 0.15),
        L / 2 - Math.max(rearNose + 1.5, L * 0.15),
        1.05,
      );
      break;
    }
    case 'tram':
      spreadRows([-W * 0.33, -W * 0.13, W * 0.13, W * 0.33], 0.9, -L * 0.3, L * 0.3, 1.6);
      break;
  }
  return seats;
}

/** 車両 1 台に乗せる人の数（車両番号ごと。{@link seatLayout} の座席の数）。 */
export function riderCount(spec: VehicleModelSpec, carIndex: number): number {
  return seatLayout(spec, carIndex).length;
}

/** 作ったモデル：車両ごとのグループ（先頭の車両が最初）と、解放の関数。 */
export interface VehicleModel {
  readonly cars: readonly Group[];
  /** 乗客の人数（全車両の合計） */
  readonly riders: number;
  /**
   * 走行の状態（走った距離・こいでいるか）に合わせて、動く部品（自転車の車輪・ペダル・こぐ脚）を動かす。
   * 動く部品のない車両では、何もしない。
   */
  setMotion(motion: VehicleMotion): void;
  dispose(): void;
}

// よく使う色
const BLACK = 0x161616;
const DARK = 0x2a2d33;
const STEEL = 0x9aa4ad;
const CHROME = 0xd6dbe0;
const RUBBER = 0x1b1b1b;
const BRASS = 0xb8913a;
const HEADLIGHT = 0xfff4cf;
const TAILLIGHT = 0xff2a2a;
const GLASS = 0x3a5a78;
const SEAT = 0x1e3a8a;

/**
 * 車両の 3D モデルを作る。局所座標は、前方 −Z・上 +Y・右 +X。原点は車両の中心の、レールの高さ。
 *
 * 1 両の部品は、仕上げ（塗装・つや消し・窓・ライト）ごとに 1 つの形状にまとめる（描画の負荷を抑える）。
 * 列車の車体は、窓の部分を抜いた中空の箱で作り、窓（半透明）から中の乗客が見える。
 * 形状・材質は `dispose` で解放する。
 */
export function buildVehicleModel(spec: VehicleModelSpec): VehicleModel {
  const materials: Record<Finish, Material> = {
    paint: new MeshStandardMaterial({ vertexColors: true, metalness: 0.35, roughness: 0.35 }),
    matte: new MeshStandardMaterial({ vertexColors: true, metalness: 0, roughness: 0.85 }),
    glass: new MeshStandardMaterial({
      vertexColors: true,
      metalness: 0.3,
      roughness: 0.05,
      transparent: true,
      opacity: 0.38,
      depthWrite: false,
    }),
    light: new MeshBasicMaterial({ vertexColors: true }),
  };
  const geometries: BufferGeometry[] = [];
  const cars: Group[] = [];
  const motions: ((motion: VehicleMotion) => void)[] = [];
  let riders = 0;
  /** 動く部品を、仕上げごとのメッシュにまとめて、グループに入れる */
  const meshesOf = (parts: PartList): Group => {
    const group = new Group();
    for (const [finish, geometry] of parts.merge()) {
      geometries.push(geometry);
      const mesh = new Mesh(geometry, materials[finish]);
      mesh.castShadow = finish !== 'glass';
      mesh.receiveShadow = finish !== 'light';
      mesh.name = finish;
      group.add(mesh);
    }
    return group;
  };

  for (let c = 0; c < spec.carCount; c += 1) {
    const parts = new PartList();
    buildCar(parts, spec, c);
    const seats = seatLayout(spec, c);
    let cyclist: { hip: P3; look: PersonLook } | undefined;
    seats.forEach((seat, i) => {
      const index = c * 17 + i;
      const look =
        seat.pose === 'cyclist'
          ? personLook(index, 'helmet', 0xf5f5f5)
          : seat.pose === 'tuck'
            ? personLook(index, 'helmet', spec.color)
            : seat.pose === 'standing'
              ? personLook(index, 'cap', 0x1f2a44)
              : personLook(index);
      const hip: P3 = [seat.x, seat.y, seat.z];
      if (seat.pose === 'cyclist') {
        // こぐ人の脚は、ペダルの位置に合わせて、動く部品として別に作る
        addPerson(parts, hip, seat.pose, look, { legs: false });
        cyclist = { hip, look };
      } else {
        addPerson(parts, hip, seat.pose, look);
      }
    });
    riders += seats.length;

    const car = meshesOf(parts);
    if (spec.model === 'bicycle') motions.push(addBicycleMotion(car, cyclist, meshesOf));
    cars.push(car);
  }

  return {
    cars,
    riders,
    setMotion(motion) {
      for (const apply of motions) apply(motion);
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of Object.values(materials)) material.dispose();
    },
  };
}

/**
 * 自転車の動く部品を作って、車に加える：前後の車輪（走った距離に合わせて回る）と、こぐ部分
 * （{@link PEDAL_PHASES} 段階の形を作っておき、人がこいでいる間は、距離に合わせて入れ替える）。
 * こがないとき（巻き上げ・ブレーキ・高速の惰性）は、そのときの形のままにする。
 * 返す関数が、走行の状態を反映する。
 */
function addBicycleMotion(
  car: Group,
  rider: { hip: P3; look: PersonLook } | undefined,
  meshesOf: (parts: PartList) => Group,
): (motion: VehicleMotion) => void {
  const animated = new Group();
  animated.name = 'animated';
  const wheels: Group[] = [];
  for (const axle of [BICYCLE_REAR_AXLE, BICYCLE_FRONT_AXLE]) {
    const parts = new PartList();
    bicycleWheel(parts);
    const wheel = meshesOf(parts);
    wheel.position.set(...axle);
    wheels.push(wheel);
    animated.add(wheel);
  }
  const phases: Group[] = [];
  for (let phase = 0; phase < PEDAL_PHASES; phase += 1) {
    const parts = new PartList();
    bicycleDrivetrain(parts, phase, rider);
    const group = meshesOf(parts);
    group.visible = phase === 0;
    phases.push(group);
    animated.add(group);
  }
  car.add(animated);
  let current = 0;
  return (motion) => {
    // 前へ進むと、車輪の上が前へ回る（X 軸まわりの負の向き）
    for (const wheel of wheels) wheel.rotation.x = -motion.distance / BICYCLE_WHEEL_RADIUS;
    if (!motion.pedaling) return;
    const next = pedalPhase(motion.distance);
    if (next === current) return;
    const previous = phases[current];
    const target = phases[next];
    if (previous) previous.visible = false;
    if (target) target.visible = true;
    current = next;
  };
}

/** 1 両の車体（乗客を除く）を、部品の集まりに加える。 */
export function buildCar(parts: PartList, spec: VehicleModelSpec, carIndex: number): void {
  const lead = carIndex === 0;
  const last = carIndex === spec.carCount - 1;
  switch (spec.model) {
    case 'coaster':
      coasterCar(parts, spec, lead);
      break;
    case 'mine-cart':
      mineCart(parts, spec, lead);
      break;
    case 'steam-locomotive':
      if (carIndex === 0) locomotive(parts, spec);
      else if (carIndex === 1) tender(parts, spec);
      else coach(parts, spec);
      break;
    case 'bullet-train':
    case 'maglev':
      highSpeedCar(parts, spec, lead, last, carIndex);
      break;
    case 'tram':
      tramCar(parts, spec, lead, last);
      break;
    case 'sports-car':
      sportsCar(parts, spec);
      break;
    case 'bicycle':
      bicycle(parts, spec);
      break;
    case 'bobsled':
      bobsled(parts, spec);
      break;
  }
}

// ───────────────────────── 共通の部品 ─────────────────────────

/** スポーク付きの車輪（軸は左右）。 */
function spokedWheel(
  parts: PartList,
  center: P3,
  radius: number,
  options: {
    readonly width?: number;
    readonly tire?: number;
    readonly disc?: number;
    readonly spokes?: number;
  } = {},
): void {
  const width = options.width ?? 0.12;
  const spokes = options.spokes ?? 10;
  parts.cylinder(radius, radius, width, 'x', options.tire ?? RUBBER, 'paint', center, 24);
  parts.cylinder(
    radius * 0.86,
    radius * 0.86,
    width * 1.05,
    'x',
    options.disc ?? DARK,
    'paint',
    center,
    24,
  );
  for (let i = 0; i < spokes; i += 1) {
    const angle = (i * Math.PI) / spokes;
    parts.box([width * 1.15, radius * 1.65, radius * 0.07], options.tire ?? RUBBER, 'paint', {
      position: center,
      rotation: [angle, 0, 0],
    });
  }
  parts.cylinder(radius * 0.18, radius * 0.18, width * 1.6, 'x', CHROME, 'paint', center, 12);
}

/** 鉄道の台車（2 軸・4 輪）。中心の Z と、車輪の半径。 */
function bogie(parts: PartList, z: number, radius: number, frameColor = DARK): void {
  parts.box([1.9, 0.28, 2.5], frameColor, 'paint', { position: [0, radius + 0.12, z] });
  for (const dz of [-0.9, 0.9]) {
    for (const x of [-0.62, 0.62]) {
      parts.cylinder(radius, radius, 0.12, 'x', BLACK, 'paint', [x, radius, z + dz], 20);
      parts.cylinder(
        radius * 0.3,
        radius * 0.3,
        0.16,
        'x',
        STEEL,
        'paint',
        [x, radius, z + dz],
        10,
      );
    }
    parts.box([0.3, 0.25, 0.4], STEEL, 'paint', { position: [-0.85, radius, z + dz] });
    parts.box([0.3, 0.25, 0.4], STEEL, 'paint', { position: [0.85, radius, z + dz] });
  }
  // ばね
  for (const x of [-0.85, 0.85]) {
    parts.cylinder(0.12, 0.12, 0.25, 'y', 0x444444, 'paint', [x, radius + 0.35, z], 10);
  }
}

/** パンタグラフ（屋根の上の集電装置）。 */
function pantograph(parts: PartList, roofY: number, z: number): void {
  parts.box([0.9, 0.15, 1.2], DARK, 'paint', { position: [0, roofY + 0.08, z] });
  for (const x of [-0.3, 0.3]) {
    parts.tube([x, roofY + 0.15, z + 0.5], [x * 0.6, roofY + 0.75, z - 0.3], 0.025, STEEL, 'paint');
    parts.tube(
      [x * 0.6, roofY + 0.75, z - 0.3],
      [x * 0.3, roofY + 1.3, z + 0.05],
      0.02,
      STEEL,
      'paint',
    );
  }
  parts.box([1.5, 0.04, 0.18], CHROME, 'paint', { position: [0, roofY + 1.32, z + 0.05] });
}

/** 中空の箱型車体の仕様（列車・路面電車・客車）。窓の部分は抜いて、半透明のガラスを入れる。 */
interface HollowBody {
  /** 前端・後端の Z（前端 < 後端） */
  readonly zFront: number;
  readonly zRear: number;
  readonly width: number;
  /** 床・窓の下端・窓の上端・壁の上端（屋根の付け根）の高さ */
  readonly floorY: number;
  readonly sillY: number;
  readonly topY: number;
  readonly roofY: number;
  /** 屋根の丸みの高さ */
  readonly roofRise: number;
  /** 床下（機器・スカート）の下端 */
  readonly bottomY: number;
  readonly lowerColor: number;
  readonly upperColor: number;
  readonly roofColor: number;
  readonly skirtColor: number;
  /** 窓の間隔と幅 */
  readonly windowPitch: number;
  readonly windowWidth: number;
  /** 窓を空けない、両端の長さ */
  readonly endInset: number;
  /** ドア（中心の Z の列）。ドアは床から窓の上端まで */
  readonly doors?: readonly number[];
  readonly doorWidth?: number;
  /** 前端・後端に壁を付けるか */
  readonly frontWall?: boolean;
  readonly rearWall?: boolean;
  /** 座席の列：最初の列の Z（乗客の腰の位置）と間隔。省略は座席なし */
  readonly seats?: { readonly start: number; readonly pitch: number };
}

function hollowBody(parts: PartList, b: HollowBody): void {
  const length = b.zRear - b.zFront;
  const centerZ = (b.zFront + b.zRear) / 2;
  const wall = 0.08;
  const half = b.width / 2;
  // 床と床下
  parts.box([b.width - 0.04, 0.1, length], 0x3f3f46, 'matte', {
    position: [0, b.floorY - 0.05, centerZ],
  });
  parts.box([b.width * 0.94, b.floorY - b.bottomY - 0.1, length * 0.98], b.skirtColor, 'paint', {
    position: [0, (b.floorY - 0.1 + b.bottomY) / 2, centerZ],
  });
  for (const side of [-1, 1]) {
    const x = side * (half - wall / 2);
    // 窓の下・上の壁
    parts.box([wall, b.sillY - b.floorY, length], b.lowerColor, 'paint', {
      position: [x, (b.sillY + b.floorY) / 2, centerZ],
    });
    parts.box([wall, b.roofY - b.topY, length], b.upperColor, 'paint', {
      position: [x, (b.roofY + b.topY) / 2, centerZ],
    });
  }
  // 窓とドアの位置を決め、その間を柱で埋める
  const doorWidth = b.doorWidth ?? 1.2;
  const openings: { z: number; width: number; door: boolean }[] = [];
  for (const z of b.doors ?? []) openings.push({ z, width: doorWidth, door: true });
  for (
    let z = b.zFront + b.endInset + b.windowWidth / 2;
    z <= b.zRear - b.endInset - b.windowWidth / 2;
    z += b.windowPitch
  ) {
    if (openings.some((o) => Math.abs(o.z - z) < (o.width + b.windowWidth) / 2 + 0.1)) continue;
    openings.push({ z, width: b.windowWidth, door: false });
  }
  openings.sort((a, c) => a.z - c.z);
  let cursor = b.zFront;
  const fill = (from: number, to: number, y0: number, y1: number, color: number): void => {
    if (to - from < 0.01 || y1 - y0 < 0.01) return;
    for (const side of [-1, 1]) {
      parts.box([wall, y1 - y0, to - from], color, 'paint', {
        position: [side * (half - wall / 2), (y0 + y1) / 2, (from + to) / 2],
      });
    }
  };
  for (const opening of openings) {
    const from = opening.z - opening.width / 2;
    const to = opening.z + opening.width / 2;
    fill(cursor, from, b.sillY, b.topY, b.upperColor);
    const bottom = opening.door ? b.floorY : b.sillY;
    if (opening.door) fill(from, to, b.floorY, b.sillY, b.lowerColor);
    for (const side of [-1, 1]) {
      parts.box([0.02, b.topY - bottom, opening.width], GLASS, 'glass', {
        position: [side * (half - wall / 2), (b.topY + bottom) / 2, opening.z],
      });
      if (opening.door) {
        // ドアの縁
        parts.box([wall + 0.02, b.topY - b.floorY, 0.06], DARK, 'paint', {
          position: [side * (half - wall / 2), (b.topY + b.floorY) / 2, from],
        });
        parts.box([wall + 0.02, b.topY - b.floorY, 0.06], DARK, 'paint', {
          position: [side * (half - wall / 2), (b.topY + b.floorY) / 2, to],
        });
      }
    }
    cursor = to;
  }
  fill(cursor, b.zRear, b.sillY, b.topY, b.upperColor);
  // 屋根（丸み）
  const roof: [number, number][] = [];
  for (let i = 0; i <= 16; i += 1) {
    const x = -half + (b.width * i) / 16;
    const u = (2 * x) / b.width;
    roof.push([x, b.roofY + b.roofRise * (1 - u * u)]);
  }
  roof.push([half, b.roofY - 0.05], [-half, b.roofY - 0.05]);
  parts.section(roof, length, b.roofColor, 'paint', centerZ);
  // 端の壁（貫通扉の窓つき）
  for (const [enabled, z] of [
    [b.frontWall, b.zFront + wall / 2],
    [b.rearWall, b.zRear - wall / 2],
  ] as const) {
    if (!enabled) continue;
    parts.box([b.width, b.roofY - b.floorY, wall], b.upperColor, 'paint', {
      position: [0, (b.roofY + b.floorY) / 2, z],
    });
  }
  // 座席（座面と背もたれ）。乗客の腰の位置にそろえる
  if (b.seats) {
    for (let z = b.seats.start; z < b.zRear - 0.4; z += b.seats.pitch) {
      if (z < b.zFront + 0.3) continue;
      for (const side of [-1, 1]) {
        parts.box([b.width * 0.36, 0.62, 0.1], SEAT, 'matte', {
          position: [side * b.width * 0.23, b.floorY + 0.75, z + 0.32],
        });
        parts.box([b.width * 0.36, 0.1, 0.45], SEAT, 'matte', {
          position: [side * b.width * 0.23, b.floorY + 0.37, z + 0.05],
        });
      }
    }
  }
}

// ───────────────────────── コースター・トロッコ ─────────────────────────

function coasterCar(parts: PartList, spec: VehicleModelSpec, lead: boolean): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  // 車体（浴槽型）：前は丸く低く、後ろは高い
  parts.side(
    [
      [-half, 0.28],
      [half - 0.3, 0.28],
      ...curve([half - 0.05, 0.3], [half + 0.08, 0.45], [half - 0.05, 0.62], 5),
      ...curve([half - 0.15, 0.7], [half - 0.3, 0.8], [half - 0.55, 0.78], 4),
      [-half + 0.12, 0.74],
      [-half, 0.66],
    ],
    W * 0.96,
    spec.color,
    'paint',
    { bevel: 0.08 },
  );
  // 白い帯と、先頭車の飾り
  parts.box([W * 0.98, 0.06, L * 0.86], 0xf5f5f5, 'paint', { position: [0, 0.5, 0] });
  if (lead) {
    parts.box([W * 0.5, 0.08, 0.06], 0xf2b705, 'light', { position: [0, 0.52, -half - 0.02] });
  }
  // 座席の背もたれ・ヘッドレスト・安全バー（席の列ごと）
  const seats = seatLayout(spec, 0);
  const rows = [...new Set(seats.map((s) => s.z))];
  for (const z of rows.length > 0 ? rows : [0]) {
    parts.box([W * 0.82, 0.62, 0.1], SEAT, 'matte', { position: [0, 0.98, z + 0.3] });
    parts.box([W * 0.82, 0.2, 0.16], 0x111827, 'matte', { position: [0, 1.36, z + 0.3] });
    parts.tube([-W * 0.36, 0.86, z - 0.3], [W * 0.36, 0.86, z - 0.3], 0.03, 0xf2b705, 'paint');
    parts.tube([0, 0.62, z - 0.18], [0, 0.86, z - 0.3], 0.03, 0xf2b705, 'paint');
  }
  // 台車：走行輪（レールの上）・側輪・浮き上がり防止輪（レールの下）
  parts.box([W * 0.62, 0.12, L * 0.9], 0x4b5563, 'paint', { position: [0, 0.22, 0] });
  for (const z of [-L * 0.32, L * 0.32]) {
    for (const side of [-1, 1]) {
      const x = side * 0.6;
      parts.box([0.1, 0.46, 0.22], 0x4b5563, 'paint', { position: [side * 0.69, 0.02, z] });
      parts.cylinder(0.1, 0.1, 0.08, 'x', 0xe5e7eb, 'matte', [x, 0.16, z], 16);
      parts.cylinder(0.07, 0.07, 0.08, 'x', 0xe5e7eb, 'matte', [x, -0.15, z], 14);
      parts.cylinder(0.07, 0.07, 0.07, 'y', 0xe5e7eb, 'matte', [side * 0.74, 0, z], 14);
    }
  }
}

function mineCart(parts: PartList, spec: VehicleModelSpec, lead: boolean): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  const wood = 0x8b5a2b;
  const iron = 0x3b3b3b;
  // 木の箱（上が開いている。中に乗客が座る）
  const wall = 0.06;
  parts.box([W, 0.06, L], 0x5b3a1e, 'matte', { position: [0, 0.33, 0] });
  for (const side of [-1, 1]) {
    parts.box([wall, 0.62, L], wood, 'matte', { position: [side * (W / 2 - wall / 2), 0.61, 0] });
    parts.box([W, 0.62, wall], wood, 'matte', { position: [0, 0.61, side * (half - wall / 2)] });
  }
  // 板の継ぎ目・鉄の帯・縁
  for (const y of [0.46, 0.62, 0.78]) {
    for (const side of [-1, 1]) {
      parts.box([0.02, 0.025, L * 1.02], 0x5b3a1e, 'matte', {
        position: [side * (W / 2 + 0.005), y, 0],
      });
    }
  }
  for (const z of [-half + 0.05, 0, half - 0.05]) {
    for (const side of [-1, 1]) {
      parts.box([0.03, 0.66, 0.08], iron, 'paint', { position: [side * (W / 2 + 0.02), 0.62, z] });
    }
  }
  // 縁の鉄の枠
  for (const side of [-1, 1]) {
    parts.box([0.1, 0.05, L + 0.1], iron, 'paint', { position: [side * (W / 2), 0.94, 0] });
    parts.box([W + 0.1, 0.05, 0.1], iron, 'paint', { position: [0, 0.94, side * half] });
  }
  // 鉄の車輪（スポーク）
  for (const z of [-L * 0.3, L * 0.3]) {
    for (const side of [-1, 1]) {
      spokedWheel(parts, [side * 0.6, 0.22, z], 0.22, {
        width: 0.08,
        tire: iron,
        disc: iron,
        spokes: 6,
      });
    }
  }
  // 先頭のトロッコのカンテラ
  if (lead) {
    parts.box([0.16, 0.22, 0.16], iron, 'paint', { position: [W * 0.3, 1.08, -half] });
    parts.sphere(0.07, 0xffc861, 'light', { position: [W * 0.3, 1.08, -half] });
  }
}

// ───────────────────────── 蒸気機関車 ─────────────────────────

function locomotive(parts: PartList, spec: VehicleModelSpec): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  const body = spec.color;
  const boilerY = 2.25;
  const boilerFront = -half + 1.8;
  const boilerRear = half - 3.6;
  // 台枠・ランボード
  parts.box([W * 0.62, 0.35, L * 0.94], BLACK, 'paint', { position: [0, 1.15, 0] });
  for (const side of [-1, 1]) {
    parts.box([0.5, 0.05, boilerRear - boilerFront], BLACK, 'paint', {
      position: [side * (W / 2 - 0.25), 1.48, (boilerFront + boilerRear) / 2],
    });
  }
  // ボイラー・帯・煙室・煙室扉
  parts.cylinder(
    0.82,
    0.82,
    boilerRear - boilerFront,
    'z',
    body,
    'paint',
    [0, boilerY, (boilerFront + boilerRear) / 2],
    28,
  );
  for (let i = 0; i <= 4; i += 1) {
    const z = boilerFront + 0.4 + ((boilerRear - boilerFront - 0.8) * i) / 4;
    parts.cylinder(0.84, 0.84, 0.06, 'z', BRASS, 'paint', [0, boilerY, z], 28);
  }
  parts.cylinder(0.86, 0.86, 1.3, 'z', BLACK, 'paint', [0, boilerY, boilerFront - 0.6], 28);
  parts.cylinder(0.72, 0.72, 0.1, 'z', 0x262626, 'paint', [0, boilerY, boilerFront - 1.28], 28);
  parts.box([0.5, 0.18, 0.04], BRASS, 'paint', {
    position: [0, boilerY - 0.25, boilerFront - 1.34],
  });
  parts.cylinder(0.06, 0.06, 0.14, 'z', CHROME, 'paint', [0.35, boilerY, boilerFront - 1.38], 10);
  // 煙突・蒸気ドーム・砂箱・汽笛
  parts.lathe(
    [
      [0.24, 0],
      [0.24, 0.55],
      [0.3, 0.68],
      [0.34, 0.8],
      [0.3, 0.86],
      [0, 0.86],
    ],
    BLACK,
    'paint',
    { position: [0, boilerY + 0.62, boilerFront - 0.6] },
  );
  parts.sphere(0.42, BLACK, 'paint', {
    position: [0, boilerY + 0.72, -half * 0.25],
    scale: [1, 0.72, 1.25],
  });
  parts.sphere(0.36, BLACK, 'paint', {
    position: [0, boilerY + 0.7, half * 0.05],
    scale: [1, 0.7, 1.1],
  });
  parts.cylinder(0.05, 0.07, 0.35, 'y', BRASS, 'paint', [0, boilerY + 1.0, half * 0.05], 10);
  // 前照灯
  parts.cylinder(0.18, 0.18, 0.3, 'z', BLACK, 'paint', [0, boilerY + 0.95, boilerFront - 1.0], 16);
  parts.cylinder(
    0.14,
    0.14,
    0.04,
    'z',
    HEADLIGHT,
    'light',
    [0, boilerY + 0.95, boilerFront - 1.16],
    16,
  );
  // 排障器（カウキャッチャー）と緩衝器
  parts.side(
    [
      [half - 0.35, 0.12],
      [half - 0.35, 0.95],
      [half + 0.5, 0.12],
    ],
    W * 0.7,
    0x9b1c1c,
    'paint',
    { bevel: 0.03 },
  );
  parts.box([W * 0.9, 0.32, 0.18], 0x9b1c1c, 'paint', { position: [0, 1.0, -half + 0.25] });
  for (const side of [-1, 1]) {
    parts.cylinder(0.12, 0.12, 0.35, 'z', DARK, 'paint', [side * 0.95, 1.0, -half + 0.05], 12);
  }
  // シリンダーと主連棒・連結棒
  for (const side of [-1, 1]) {
    parts.cylinder(0.32, 0.32, 1.1, 'z', BLACK, 'paint', [side * 1.0, 1.05, boilerFront + 0.2], 18);
  }
  // 動輪（3 軸）・先輪・従輪
  const drivers = [-1.1, 0.75, 2.6].map((dz) => boilerFront + 1.6 + dz);
  for (const z of drivers) {
    for (const side of [-1, 1]) {
      spokedWheel(parts, [side * 0.7, 0.85, z], 0.85, {
        width: 0.14,
        tire: BLACK,
        disc: 0xa61b1b,
        spokes: 12,
      });
    }
  }
  for (const side of [-1, 1]) {
    parts.tube(
      [side * 0.84, 1.05, drivers[0] ?? 0],
      [side * 0.84, 1.05, drivers[2] ?? 0],
      0.045,
      STEEL,
      'paint',
    );
    parts.tube(
      [side * 0.9, 1.05, boilerFront + 0.2],
      [side * 0.9, 1.05, drivers[1] ?? 0],
      0.05,
      STEEL,
      'paint',
    );
    spokedWheel(parts, [side * 0.65, 0.45, boilerFront - 0.6], 0.45, {
      width: 0.1,
      tire: BLACK,
      disc: 0xa61b1b,
      spokes: 8,
    });
    spokedWheel(parts, [side * 0.65, 0.55, half - 1.6], 0.55, {
      width: 0.1,
      tire: BLACK,
      disc: 0xa61b1b,
      spokes: 8,
    });
  }
  // 運転台（窓・屋根）
  const cabFront = half - 3.6;
  const cabZ = (cabFront + half) / 2;
  hollowBody(parts, {
    zFront: cabFront,
    zRear: half,
    width: W,
    floorY: 1.45,
    sillY: 2.55,
    topY: 3.25,
    roofY: 3.6,
    roofRise: 0.2,
    bottomY: 1.3,
    lowerColor: body,
    upperColor: body,
    roofColor: BLACK,
    skirtColor: BLACK,
    windowPitch: 1.4,
    windowWidth: 0.9,
    endInset: 0.5,
    frontWall: true,
  });
  parts.box([0.5, 0.4, 0.04], GLASS, 'glass', { position: [-0.8, 3.0, cabFront - 0.02] });
  parts.box([0.5, 0.4, 0.04], GLASS, 'glass', { position: [0.8, 3.0, cabFront - 0.02] });
  parts.box([0.4, 0.15, 0.04], BRASS, 'paint', {
    position: [W / 2 + 0.01, 2.0, cabZ],
    rotation: [0, Math.PI / 2, 0],
  });
}

function tender(parts: PartList, spec: VehicleModelSpec): void {
  const L = spec.carLength;
  const W = spec.width;
  parts.box([W * 0.96, 1.7, L * 0.9], spec.color, 'paint', { position: [0, 2.05, 0] });
  parts.box([W, 0.08, L * 0.92], BLACK, 'paint', { position: [0, 2.92, 0] });
  // 石炭の山
  parts.sphere(1, 0x0d0d0d, 'matte', {
    position: [0, 2.9, -L * 0.15],
    scale: [W * 0.42, 0.45, L * 0.32],
  });
  parts.cylinder(0.3, 0.3, 0.2, 'y', BLACK, 'paint', [0, 3.0, L * 0.3], 14);
  parts.box([W * 0.7, 0.3, L * 0.9], BLACK, 'paint', { position: [0, 1.1, 0] });
  bogie(parts, -L * 0.3, 0.45, BLACK);
  bogie(parts, L * 0.3, 0.45, BLACK);
}

function coach(parts: PartList, spec: VehicleModelSpec): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  hollowBody(parts, {
    zFront: -half,
    zRear: half,
    width: W,
    floorY: 1.0,
    sillY: 1.75,
    topY: 2.55,
    roofY: 3.15,
    roofRise: 0.4,
    bottomY: 0.75,
    lowerColor: 0x6b1d1d,
    upperColor: 0x6b1d1d,
    roofColor: 0x3a3a3a,
    skirtColor: BLACK,
    windowPitch: 1.6,
    windowWidth: 1.0,
    endInset: 1.2,
    doors: [-half + 0.65, half - 0.65],
    doorWidth: 0.75,
    frontWall: true,
    rearWall: true,
    seats: { start: -L * 0.36, pitch: 1.6 },
  });
  parts.box([W * 1.01, 0.06, L], BRASS, 'paint', { position: [0, 1.7, 0] });
  parts.box([1.0, 2.0, 0.6], BLACK, 'matte', { position: [0, 2.0, half + 0.25] });
  bogie(parts, -half + 2.4, 0.45, BLACK);
  bogie(parts, half - 2.4, 0.45, BLACK);
}

// ───────────────────────── 新幹線・リニア ─────────────────────────

function highSpeedCar(
  parts: PartList,
  spec: VehicleModelSpec,
  lead: boolean,
  last: boolean,
  carIndex: number,
): void {
  const maglev = spec.model === 'maglev';
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  const white = maglev ? 0xf5f7fa : spec.color;
  const stripe = maglev ? spec.color : 0x1d4ed8;
  const floorY = maglev ? 1.0 : 1.1;
  const roofY = spec.height - 0.35;
  const rise = 0.35;
  const bottomY = maglev ? 0.18 : 0.55;
  const nose = noseLength(spec.model, L);
  const frontNose = lead ? nose : 0;
  const rearNose = last && spec.carCount > 1 ? nose : 0;
  hollowBody(parts, {
    zFront: -half + frontNose,
    zRear: half - rearNose,
    width: W,
    floorY,
    sillY: maglev ? 1.85 : 1.8,
    topY: maglev ? 2.3 : 2.4,
    roofY,
    roofRise: rise,
    bottomY,
    lowerColor: white,
    upperColor: white,
    roofColor: white,
    skirtColor: maglev ? 0xd1d5db : 0x9ca3af,
    windowPitch: 1.05,
    windowWidth: maglev ? 0.5 : 0.75,
    endInset: 1.6,
    doors: [-half + frontNose + 1.0, half - rearNose - 1.0],
    doorWidth: 0.9,
    frontWall: frontNose === 0,
    rearWall: rearNose === 0,
    seats: {
      start: -half + (lead ? Math.max(nose + 1.5, L * 0.15) : L * 0.15),
      pitch: 1.05,
    },
  });
  // 鼻（流線形）：横から見た輪郭を押し出す
  const noseProfile = (direction: 1 | -1): [number, number][] => {
    // direction 1：前（s が正）へ伸びる鼻。根元は s0
    const s0 = direction * (half - nose);
    const tip = direction * half;
    const top = roofY + rise;
    const at = (d: number): number => s0 + direction * d;
    const points: [number, number][] = [
      [s0, bottomY],
      [at(nose - 0.6), bottomY],
      ...curve(
        [tip, bottomY + 0.15],
        [tip + direction * 0.15, bottomY + 0.35],
        [tip, bottomY + 0.55],
        4,
      ),
      ...curve(
        [at(nose * 0.9), bottomY + 0.75],
        [at(nose * 0.55), (bottomY + top) * 0.45],
        [at(nose * 0.38), top - 0.55],
        8,
      ),
      ...curve([at(nose * 0.3), top - 0.4], [at(nose * 0.12), top], [s0, top], 6),
    ];
    return direction === 1 ? points : points.reverse();
  };
  if (frontNose > 0) {
    parts.side(noseProfile(1), W, white, 'paint', { bevel: 0.45 });
    windshield(parts, -half + nose * 0.68, roofY + rise - 0.42, W, maglev);
    for (const side of [-1, 1]) {
      parts.box([0.06, 0.14, 0.6], HEADLIGHT, 'light', {
        position: [side * (W / 2 - 0.02), bottomY + 0.62, -half + 1.4],
      });
    }
  }
  if (rearNose > 0) {
    parts.side(noseProfile(-1), W, white, 'paint', { bevel: 0.45 });
    windshield(parts, half - nose * 0.68, roofY + rise - 0.42, W, maglev, true);
    for (const side of [-1, 1]) {
      parts.box([0.06, 0.14, 0.6], TAILLIGHT, 'light', {
        position: [side * (W / 2 - 0.02), bottomY + 0.62, half - 1.4],
      });
    }
  }
  // 帯（太い帯と細い帯）
  const bandFrom = -half + frontNose * 0.55;
  const bandTo = half - rearNose * 0.55;
  for (const side of [-1, 1]) {
    parts.box([0.02, maglev ? 0.35 : 0.2, bandTo - bandFrom], stripe, 'paint', {
      position: [side * (W / 2 + 0.005), maglev ? 1.2 : 1.55, (bandFrom + bandTo) / 2],
    });
    if (!maglev) {
      parts.box([0.02, 0.05, bandTo - bandFrom], stripe, 'paint', {
        position: [side * (W / 2 + 0.005), 1.35, (bandFrom + bandTo) / 2],
      });
    }
  }
  if (maglev) {
    // 浮上走行：車輪はなく、深いスカートが走路の近くまで下がる
    parts.box([W * 0.9, 0.2, L * 0.85], 0x6b7280, 'paint', { position: [0, bottomY + 0.05, 0] });
  } else {
    bogie(parts, -half + Math.max(frontNose * 0.75, 3.2), 0.43);
    bogie(parts, half - Math.max(rearNose * 0.75, 3.2), 0.43);
    if (carIndex % 2 === 1) pantograph(parts, roofY + rise, 0);
  }
  if (!last) {
    parts.box([1.4, 2.2, spec.carSpacing + 0.2], 0x2a2a2a, 'matte', {
      position: [0, floorY + 1.1, half + spec.carSpacing / 2],
    });
  }
}

/** 運転席の窓（鼻の上の、傾いたガラス）。 */
function windshield(
  parts: PartList,
  z: number,
  y: number,
  width: number,
  maglev: boolean,
  rear = false,
): void {
  const tilt = maglev ? 0.22 : 0.3;
  parts.box([width * 0.62, 0.05, maglev ? 1.4 : 2.0], 0x0f1b2a, 'paint', {
    position: [0, y, z],
    rotation: [rear ? tilt : -tilt, 0, 0],
  });
}

// ───────────────────────── 路面電車 ─────────────────────────

function tramCar(parts: PartList, spec: VehicleModelSpec, lead: boolean, last: boolean): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  const roofY = spec.height - 0.25;
  hollowBody(parts, {
    zFront: -half + (lead ? 1.0 : 0),
    zRear: half - (last ? 1.0 : 0),
    width: W,
    floorY: 0.45,
    sillY: 1.05,
    topY: 2.6,
    roofY,
    roofRise: 0.15,
    bottomY: 0.2,
    lowerColor: spec.color,
    upperColor: 0xf5f5f5,
    roofColor: 0xd1d5db,
    skirtColor: spec.color,
    windowPitch: 1.6,
    windowWidth: 1.35,
    endInset: 0.3,
    doors: [-half * 0.45, half * 0.45],
    doorWidth: 1.3,
    frontWall: !lead,
    rearWall: !last,
    seats: { start: -L * 0.3, pitch: 1.6 },
  });
  // 運転台（前後の丸い顔）
  for (const [enabled, direction] of [
    [lead, 1],
    [last, -1],
  ] as const) {
    if (!enabled) continue;
    const s0 = direction * (half - 1.0);
    const tip = direction * half;
    const profile: [number, number][] = [
      [s0, 0.2],
      [tip, 0.2],
      ...curve([tip, 0.9], [tip + direction * 0.05, 1.8], [tip - direction * 0.35, 2.75], 6),
      [s0, roofY + 0.12],
    ];
    parts.side(direction === 1 ? profile : profile.reverse(), W, spec.color, 'paint', {
      bevel: 0.2,
    });
    parts.box([W * 0.86, 1.3, 0.05], GLASS, 'glass', {
      position: [0, 1.9, -direction * (half - 0.08)],
      rotation: [direction * 0.18, 0, 0],
    });
    parts.box([W * 0.5, 0.18, 0.05], 0xffb000, 'light', {
      position: [0, 2.72, -direction * (half - 0.32)],
    });
    for (const side of [-1, 1]) {
      parts.box([0.25, 0.12, 0.06], direction === 1 ? HEADLIGHT : TAILLIGHT, 'light', {
        position: [side * (W / 2 - 0.35), 0.75, -direction * (half + 0.01)],
      });
    }
  }
  if (lead) pantograph(parts, roofY + 0.15, -half * 0.2);
  if (!last) {
    parts.box([1.8, 2.2, spec.carSpacing + 0.25], 0x333333, 'matte', {
      position: [0, 1.5, half + spec.carSpacing / 2],
    });
  }
  bogie(parts, -half + 2.5, 0.33, spec.color);
  bogie(parts, half - 2.5, 0.33, spec.color);
}

// ───────────────────────── スポーツカー ─────────────────────────

function sportsCar(parts: PartList, spec: VehicleModelSpec): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  const wheelBase = L * 0.32;
  const wheelR = 0.34;
  const arch = (center: number): [number, number][] => {
    const points: [number, number][] = [];
    for (let i = 0; i <= 10; i += 1) {
      const a = Math.PI - (i * Math.PI) / 10;
      points.push([center + 0.42 * Math.cos(a), wheelR + 0.02 + 0.42 * Math.sin(a)]);
    }
    return points;
  };
  // 車体（ホイールアーチ付きの輪郭）
  parts.side(
    [
      [-half + 0.05, 0.3],
      ...arch(-wheelBase),
      ...arch(wheelBase),
      [half - 0.2, 0.28],
      ...curve([half, 0.38], [half + 0.06, 0.55], [half - 0.15, 0.66], 4),
      [half - 0.9, 0.78],
      [0.72, 0.9],
      [-1.35, 0.95],
      ...curve([-half + 0.25, 0.93], [-half + 0.02, 0.92], [-half, 0.72], 4),
    ],
    W,
    spec.color,
    'paint',
    { bevel: 0.14 },
  );
  // 窓（キャビン）と屋根
  parts.side(
    [
      [0.75, 0.88],
      [-0.05, 1.2],
      [-0.75, 1.22],
      [-1.45, 0.97],
    ],
    W * 0.8,
    GLASS,
    'glass',
    { bevel: 0.08 },
  );
  parts.box([W * 0.72, 0.04, 0.8], spec.color, 'paint', { position: [0, 1.23, 0.4] });
  // ライト・グリル・ミラー・ウィング・マフラー
  for (const side of [-1, 1]) {
    parts.box([0.4, 0.08, 0.2], HEADLIGHT, 'light', {
      position: [side * (W / 2 - 0.35), 0.62, -half + 0.2],
      rotation: [-0.25, 0, 0],
    });
    parts.box([0.4, 0.07, 0.04], TAILLIGHT, 'light', {
      position: [side * (W / 2 - 0.35), 0.8, half - 0.02],
    });
    parts.box([0.06, 0.1, 0.16], spec.color, 'paint', {
      position: [side * (W / 2 + 0.06), 0.98, -0.6],
    });
    parts.cylinder(0.05, 0.05, 0.15, 'z', CHROME, 'paint', [side * 0.35, 0.33, half + 0.02], 10);
  }
  parts.box([W * 0.5, 0.12, 0.05], BLACK, 'paint', { position: [0, 0.42, -half + 0.03] });
  parts.box([W * 0.9, 0.04, 0.3], BLACK, 'paint', { position: [0, 1.2, half - 0.15] });
  for (const side of [-1, 1]) {
    parts.box([0.04, 0.22, 0.08], BLACK, 'paint', { position: [side * 0.45, 1.06, half - 0.15] });
  }
  // 車輪（タイヤ・5 本スポークのホイール・ブレーキキャリパー）
  for (const z of [-wheelBase, wheelBase]) {
    for (const side of [-1, 1]) {
      const x = side * (W / 2 - 0.13);
      parts.cylinder(wheelR, wheelR, 0.25, 'x', RUBBER, 'matte', [x, wheelR, z], 24);
      parts.cylinder(0.24, 0.24, 0.26, 'x', CHROME, 'paint', [x, wheelR, z], 20);
      for (let i = 0; i < 5; i += 1) {
        parts.box([0.27, 0.36, 0.05], 0x6b7280, 'paint', {
          position: [x, wheelR, z],
          rotation: [(i * Math.PI) / 5, 0, 0],
        });
      }
      parts.box([0.06, 0.12, 0.16], 0xdc2626, 'paint', {
        position: [x - side * 0.1, wheelR + 0.12, z],
      });
    }
  }
  // 室内：座席とハンドル（右ハンドル）
  for (const side of [-1, 1]) {
    parts.box([0.5, 0.55, 0.12], 0x111111, 'matte', { position: [side * W * 0.22, 0.65, 0.62] });
  }
  parts.torus(0.17, 0.025, 'z', 0x111111, 'paint', [W * 0.22, 0.86, -0.08]);
  parts.box([W * 0.85, 0.12, 0.3], 0x111111, 'matte', { position: [0, 0.82, -0.25] });
}

// ───────────────────────── 自転車 ─────────────────────────

function bicycle(parts: PartList, spec: VehicleModelSpec): void {
  const frame = spec.color;
  // 前後の軸・クランク軸（ボトムブラケット）・シート・ヘッド（s は前方。z = −s）
  const at = (s: number, y: number, x = 0): P3 => [x, y, -s];
  const rear = at(-0.52, 0.34);
  const front = at(0.52, 0.34);
  const bb = at(0.05, 0.3);
  const seatTop = at(-0.14, 0.82);
  const headTop = at(0.42, 0.84);
  const headBottom = at(0.46, 0.64);
  // 車輪・クランク・ペダルは、動くので、別に作る（bicycleWheel・bicycleDrivetrain）
  // フレーム（ダイヤモンド型）
  parts.tube(bb, seatTop, 0.018, frame, 'paint');
  parts.tube(seatTop, headTop, 0.017, frame, 'paint');
  parts.tube(bb, headBottom, 0.022, frame, 'paint');
  parts.tube(headBottom, headTop, 0.022, frame, 'paint');
  for (const side of [-1, 1]) {
    parts.tube([side * 0.05, bb[1], bb[2]], [side * 0.06, rear[1], rear[2]], 0.011, frame, 'paint');
    parts.tube(
      [side * 0.04, seatTop[1] - 0.04, seatTop[2]],
      [side * 0.06, rear[1], rear[2]],
      0.01,
      frame,
      'paint',
    );
    parts.tube(
      [side * 0.03, headBottom[1], headBottom[2]],
      [side * 0.05, front[1], front[2]],
      0.012,
      frame,
      'paint',
    );
  }
  // サドル・ハンドル・ステム
  parts.tube(seatTop, at(-0.16, 0.92), 0.012, CHROME, 'paint');
  parts.box([0.13, 0.05, 0.27], BLACK, 'matte', { position: at(-0.15, 0.95) });
  parts.tube(headTop, at(0.52, 0.95), 0.014, CHROME, 'paint');
  parts.tube(at(0.55, 0.95, -0.22), at(0.55, 0.95, 0.22), 0.012, CHROME, 'paint');
  for (const side of [-1, 1]) {
    parts.cylinder(0.018, 0.018, 0.1, 'x', BLACK, 'matte', at(0.55, 0.95, side * 0.2), 8);
  }
  // チェーンリング・チェーン
  parts.cylinder(0.1, 0.1, 0.01, 'x', CHROME, 'paint', [0.06, bb[1], bb[2]], 24);
  parts.tube(
    [0.07, bb[1] + 0.1, bb[2]],
    [0.07, rear[1] + 0.04, rear[2]],
    0.006,
    0x444444,
    'paint',
    4,
  );
  parts.tube(
    [0.07, bb[1] - 0.1, bb[2]],
    [0.07, rear[1] - 0.04, rear[2]],
    0.006,
    0x444444,
    'paint',
    4,
  );
  // 前照灯
  parts.cylinder(0.03, 0.03, 0.05, 'z', HEADLIGHT, 'light', at(0.58, 0.82), 10);
}

/** 自転車の車輪の半径 [m]。 */
const BICYCLE_WHEEL_RADIUS = 0.33;

/** 前輪・後輪の中心の位置（車両の局所座標）。 */
const BICYCLE_REAR_AXLE: P3 = [0, 0.34, 0.52];
const BICYCLE_FRONT_AXLE: P3 = [0, 0.34, -0.52];

/** クランク（ボトムブラケット）の中心と、クランクの長さ。 */
const BICYCLE_CRANK_CENTER: P3 = [0, 0.3, -0.05];
const BICYCLE_CRANK_LENGTH = 0.17;

/** クランクが 1 回転するあいだに進む距離 [m]（ギア比 約 3.3 × 車輪の外周 約 2.07 m）。 */
export const CRANK_DISTANCE = 7;

/** こぐ動きを、何段階の形で表すか（クランク 1 回転を等分する）。 */
export const PEDAL_PHASES = 16;

/** 速度が大きいと、人はこがずに惰性で進む（これ以上では、こがない）[m/s]（約 50 km/h）。 */
export const PEDALING_MAX_SPEED = 14;

/** 動いている車両の状態：走った距離と、人がこいでいるか。 */
export interface VehicleMotion {
  /** 走った距離 [m]（車輪の回転・ペダルの位置を、ここから決める） */
  readonly distance: number;
  /** 自転車の人が、ペダルをこいでいるか（巻き上げ・ブレーキなどではこがない） */
  readonly pedaling: boolean;
}

/**
 * 走った距離から、ペダルの位置の段階（0 〜 {@link PEDAL_PHASES} − 1）を求める。前へ進むと、クランクは
 * 前へ回る（段階が増えるほど、ペダルは前から下へ回る）。距離が負・巻き戻しでも、範囲に収まる。
 */
export function pedalPhase(distance: number): number {
  const turns = distance / CRANK_DISTANCE;
  const fraction = turns - Math.floor(turns);
  return Math.min(PEDAL_PHASES - 1, Math.floor(fraction * PEDAL_PHASES));
}

/** 段階 `phase` のとき、左右のペダルの位置（`[0]` が左）。クランクの角度は、前（0）から下（負）へ回る。 */
export function pedalPositions(phase: number): readonly [P3, P3] {
  const angle = (-2 * Math.PI * phase) / PEDAL_PHASES;
  const at = (a: number, x: number): P3 => [
    x,
    BICYCLE_CRANK_CENTER[1] + BICYCLE_CRANK_LENGTH * Math.sin(a),
    BICYCLE_CRANK_CENTER[2] - BICYCLE_CRANK_LENGTH * Math.cos(a),
  ];
  return [at(angle, -0.1), at(angle + Math.PI, 0.1)];
}

/** 自転車の車輪 1 つ（中心が原点。X 軸のまわりに回す）：タイヤ・リム・スポーク・ハブ。 */
function bicycleWheel(parts: PartList): void {
  const center: P3 = [0, 0, 0];
  parts.torus(0.33, 0.022, 'x', RUBBER, 'matte', center, [8, 32]);
  parts.torus(0.31, 0.01, 'x', CHROME, 'paint', center, [6, 32]);
  for (let i = 0; i < 16; i += 1) {
    const a = (i * 2 * Math.PI) / 16;
    parts.tube(center, [0, 0.3 * Math.sin(a), 0.3 * Math.cos(a)], 0.003, CHROME, 'paint', 4);
  }
  parts.cylinder(0.025, 0.025, 0.1, 'x', CHROME, 'paint', center, 10);
}

/**
 * こぐ部分 1 段階分：左右のクランクとペダル、と、乗っている人の脚（ペダルに足を置く）。
 * `rider` がなければ、クランクとペダルだけ。
 */
function bicycleDrivetrain(
  parts: PartList,
  phase: number,
  rider: { readonly hip: P3; readonly look: PersonLook } | undefined,
): void {
  const pedals = pedalPositions(phase);
  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    const pedal = pedals[i];
    parts.tube(
      [side * 0.08, BICYCLE_CRANK_CENTER[1], BICYCLE_CRANK_CENTER[2]],
      pedal,
      0.012,
      CHROME,
      'paint',
    );
    parts.box([0.09, 0.02, 0.06], BLACK, 'paint', { position: pedal });
  }
  if (rider) addCyclistLegs(parts, rider.hip, rider.look, pedals);
}

// ───────────────────────── ボブスレー ─────────────────────────

function bobsled(parts: PartList, spec: VehicleModelSpec): void {
  const L = spec.carLength;
  const W = spec.width;
  const half = L / 2;
  // 船体（低く、前が丸く上がる。上は開いた操縦席）
  parts.side(
    [
      [-half, 0.12],
      [half - 0.45, 0.12],
      ...curve([half - 0.1, 0.18], [half + 0.08, 0.32], [half - 0.05, 0.52], 5),
      ...curve([half - 0.3, 0.62], [half - 0.6, 0.68], [half - 0.9, 0.58], 4),
      [-half + 0.25, 0.56],
      [-half, 0.5],
    ],
    W + 0.12,
    spec.color,
    'paint',
    { bevel: 0.12 },
  );
  parts.box([W + 0.14, 0.05, L * 0.86], 0xf5f5f5, 'paint', { position: [0, 0.38, 0] });
  parts.box([W * 0.7, 0.04, 0.4], 0x111111, 'glass', {
    position: [0, 0.66, -half + 0.75],
    rotation: [-0.35, 0, 0],
  });
  // ランナー（刃）と押し手
  for (const side of [-1, 1]) {
    parts.box([0.04, 0.08, L * 0.9], CHROME, 'paint', { position: [side * 0.3, 0.04, 0] });
    parts.tube(
      [side * (W / 2 + 0.06), 0.45, half - 0.2],
      [side * (W / 2 + 0.06), 0.75, half + 0.1],
      0.02,
      CHROME,
      'paint',
    );
    parts.tube(
      [side * (W / 2 + 0.06), 0.45, -half + 1.1],
      [side * (W / 2 + 0.12), 0.6, -half + 1.2],
      0.02,
      CHROME,
      'paint',
    );
  }
}
